import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { SettingsResponse } from "../../shared/types";
import { SeasonCard } from "../components/season/Card";
import { DockedBar } from "../components/season/DockedBar";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import type { SeasonLayout } from "../components/season/layout";
import { Page } from "../components/season/Page";
import { PageHead } from "../components/season/PageHead";
import { Pane, Panes } from "../components/season/Panes";
import { PushedNav } from "../components/season/PushedNav";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { usePreviewWho } from "../setup/parts";
import { usePreviewRoom } from "../setup/Previews";
import { Callout } from "../setup/SeasonParts";
import { nextSetupPath, setupCount } from "../setup/SetupContext";
import { useBrandDraft } from "../setup/useBrandDraft";
import type { Run } from "../setup/useSettingsData";
import {
  ApiPane,
  AutomationsPane,
  BrandDetail,
  BrandPane,
  DomainsDetail,
  DomainsPane,
  type Frame,
  HealthRows,
  type PaneProps,
  RecordsPane,
  ScriptsDetail,
  ScriptsPane,
  SendingPane,
} from "./SeasonPanes";
import { SettingsList } from "./SettingsRows";
import { type PaneKey, SET_STEP, SNAV, stepUnset, useKeys } from "./SettingsView";
import "../styles/season-settings.css";

interface Props {
  layout: SeasonLayout;
  pane: PaneKey | null;
  sub: string | undefined;
  data: SettingsResponse;
  run: Run;
  setData: (d: SettingsResponse) => void;
}

const CLEAN: Frame = { dirty: false, saving: false, save: () => undefined, discard: () => undefined };

/** A pane that is not set up yet says so, with the way into setup. */
function NotSetUp({ pane }: { pane: PaneKey }) {
  const navigate = useNavigate();
  const step = SET_STEP[pane];
  return (
    <div className="st-gap">
      <Callout tone="warn" icon="info">
        <b>Not set up yet</b>
        <Button variant="p" onClick={() => navigate(step?.[1] ?? "/setup")}>
          Finish in setup
        </Button>
      </Callout>
    </div>
  );
}

/**
 * One pane with its frame: on a phone a pushed screen (Back, Discard, a docked Save changes), on an iPad the pane's head
 * with Discard and Save changes beside the title. Keyed by pane, so what is unsaved goes when another pane opens.
 */
function PaneHost({ layout, pane, sub, data, run, setData, brand, who }: Props & { pane: PaneKey; brand: ReturnType<typeof useBrandDraft>; who: ReturnType<typeof usePreviewWho> }) {
  const [state, setState] = useState({ dirty: false, saving: false });
  const actions = useRef<Frame>(CLEAN);
  const frame: PaneProps["frame"] = (f) => {
    actions.current = f;
    setState((s) => (s.dirty === f.dirty && s.saving === f.saving ? s : { dirty: f.dirty, saving: f.saving }));
  };
  const label = SNAV.find((p) => p.key === pane)?.label ?? "Settings";
  const records = pane === "sending" && sub === "records";
  const title = records ? "The records" : label;
  const props: PaneProps = { layout, data, run, setData, frame };
  let body: ReactNode;
  if (records) body = <RecordsPane data={data} />;
  else if (pane === "brand") body = <BrandPane layout={layout} brand={brand} who={who} data={data} frame={frame} />;
  else if (pane === "sending") body = <SendingPane {...props} />;
  else if (pane === "domains") body = <DomainsPane {...props} />;
  else if (pane === "scripts") body = <ScriptsPane {...props} />;
  else if (pane === "automations") body = <AutomationsPane {...props} />;
  else body = <ApiPane />;
  const notSet = !records && stepUnset(pane, data) ? <NotSetUp pane={pane} /> : null;
  const save = () => actions.current.save();
  const discard = () => actions.current.discard();

  if (layout === "phone") {
    const back = records ? { to: "/settings/sending", label: "Sending email" } : { to: "/settings", label: "Settings" };
    return (
      <Page layout="phone" className="st-page">
        <PushedNav
          back={back}
          title={title}
          right={
            state.dirty ? (
              <button type="button" className="k-link" onClick={discard}>
                Discard
              </button>
            ) : undefined
          }
        />
        <div className="st-body">
          {notSet}
          {body}
        </div>
        {state.dirty ? (
          <DockedBar>
            <span className="k-bar-t">
              <em>Unsaved changes</em>
              <b>{title}</b>
            </span>
            <Button variant="p" disabled={state.saving} onClick={save}>
              Save changes
            </Button>
          </DockedBar>
        ) : null}
      </Page>
    );
  }
  return (
    <>
      <div className="st-ph">
        <div>
          {records ? (
            <Link className="k-back st-pb" to="/settings/sending">
              <Icon name="left" />
              <span>Sending email</span>
            </Link>
          ) : null}
          <h1 className="k-h1">{title}</h1>
        </div>
        <span className="st-ph-a">
          {state.dirty ? (
            <>
              <Button variant="q" onClick={discard}>
                Discard
              </Button>
              <Button variant="p" disabled={state.saving} onClick={save}>
                Save changes
              </Button>
            </>
          ) : null}
        </span>
      </div>
      <div className="st-body">
        {notSet}
        {body}
      </div>
    </>
  );
}

/** The six settings pages for a phone and an iPad: a list, and each page pushed (phone), beside it (upright) or in panes (side). */
export function SeasonSettings(props: Props) {
  const { layout, pane, sub, data, run } = props;
  const navigate = useNavigate();
  const keys = useKeys();
  const brand = useBrandDraft(data, run);
  const room = usePreviewRoom();
  const baseWho = usePreviewWho(data, room);
  const who = { ...baseWho, fromName: brand.draft.fromName || baseWho.business, footer: brand.draft.footer };
  const { discard } = brand;
  useEffect(() => {
    if (pane !== "brand") discard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane]);
  const n = setupCount(data.setup);
  const finish =
    n < 4 ? (
      <GroupedList>
        <ListRow icon="wand" tone="wine" title={`Finish setup, ${n} of 4 done`} onClick={() => navigate(nextSetupPath(data.setup))} />
      </GroupedList>
    ) : null;
  const shown: PaneKey = pane ?? "brand";
  const host = <PaneHost key={`${shown}${sub ?? ""}`} {...props} pane={shown} brand={brand} who={who} />;
  const list = (
    <>
      {finish ? <div className="st-gap">{finish}</div> : null}
      <div className="st-gap">
        <SettingsList data={data} keys={keys} selected={layout === "phone" ? undefined : shown} className="st-lines" />
      </div>
    </>
  );

  if (layout === "phone") {
    if (pane) return host;
    return (
      <Page layout="phone" className="st-page">
        <PushedNav back={{ to: "/more", label: "More" }} />
        <PageHead title="Settings" />
        {list}
      </Page>
    );
  }
  if (layout === "upright") {
    return (
      <Page layout="upright" className="st-page st-up">
        <div className="st-two">
          <nav className="st-list" aria-label="Settings">
            {list}
          </nav>
          <SeasonCard className="st-card">{host}</SeasonCard>
        </div>
      </Page>
    );
  }
  const records = shown === "sending" && sub === "records";
  let detail: ReactNode = null;
  if (!records) {
    if (shown === "brand") detail = <BrandDetail brand={brand} who={who} />;
    else if (shown === "sending") detail = <HealthRows s={data.sending} />;
    else if (shown === "domains") detail = <DomainsDetail data={data} who={who} />;
    else if (shown === "scripts") detail = <ScriptsDetail data={data} />;
  }
  return (
    <Panes three={!!detail} className="st-split">
      <Pane kind="list" label="Settings" keep="settings-list">
        <div className="k-sp-lh">
          <div className="k-head">
            <h1 className="k-title">Settings</h1>
          </div>
        </div>
        <Page layout="side" className="st-page">
          {list}
        </Page>
      </Pane>
      <Pane kind="main" label={SNAV.find((p) => p.key === shown)?.label} showing={`${shown}${sub ?? ""}`}>
        <Page layout="side" className="st-page st-main">
          {host}
        </Page>
      </Pane>
      {detail ? (
        <Pane kind="detail" showing={shown}>
          <Page layout="side" className="st-page">
            {detail}
          </Page>
        </Pane>
      ) : null}
    </Panes>
  );
}
